import { Card, CardContent } from '../../components/ui';
import { useLanguageStore } from '../../store/languageStore';
import { getTranslation } from '../../i18n/translations';

export const AboutPage = () => {
  const { language } = useLanguageStore();
  const t = getTranslation(language);

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 px-4 py-20 md:py-32">
        <div className="container mx-auto text-center">
          <h1 className="mb-4 text-5xl font-black tracking-tight md:text-6xl lg:text-7xl">
            <span className="bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent">
              {t.about.hero.title}
            </span>
          </h1>
          <p className="mx-auto max-w-3xl text-xl font-semibold text-foreground md:text-2xl">
            {t.about.hero.tagline}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="bg-gradient-to-b from-background to-muted/30 px-4 py-20 md:py-24">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">{t.about.story.title}</h2>
            <p className="text-lg text-muted-foreground">{t.about.story.subtitle}</p>
          </div>
          <div className="space-y-6 text-lg leading-relaxed text-foreground">
            {t.about.story.content.map((paragraph: string, idx: number) => (
              <p key={idx} className="text-justify">{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="px-4 py-20 md:py-24">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold">{t.about.pillars.title}</h2>
            <p className="text-lg text-muted-foreground">{t.about.pillars.subtitle}</p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {/* Mission */}
            <Card className="border-none bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-lg bg-blue-500/20">
                  <span className="text-4xl">🎯</span>
                </div>
                <h3 className="mb-3 text-2xl font-bold">{t.about.mission.title}</h3>
                <p className="leading-relaxed text-foreground/90">
                  {t.about.mission.description}
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="border-none bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-lg bg-purple-500/20">
                  <span className="text-4xl">🚀</span>
                </div>
                <h3 className="mb-3 text-2xl font-bold">{t.about.vision.title}</h3>
                <p className="leading-relaxed text-foreground/90">
                  {t.about.vision.description}
                </p>
              </CardContent>
            </Card>

            {/* Values */}
            <Card className="border-none bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-950 dark:to-pink-900">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-lg bg-pink-500/20">
                  <span className="text-4xl">💎</span>
                </div>
                <h3 className="mb-3 text-2xl font-bold">{t.about.values.title}</h3>
                <p className="leading-relaxed text-foreground/90">
                  {t.about.values.description}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What Drives Us */}
      <section className="bg-muted/50 px-4 py-20 md:py-24">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold">{t.about.drives.title}</h2>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: '🤝', title: t.about.drives.collaboration.title, desc: t.about.drives.collaboration.description },
              { icon: '😊', title: t.about.drives.fun.title, desc: t.about.drives.fun.description },
              { icon: '📈', title: t.about.drives.growth.title, desc: t.about.drives.growth.description },
              { icon: '🎓', title: t.about.drives.learning.title, desc: t.about.drives.learning.description },
            ].map((item, idx) => (
              <Card key={idx} className="border-none text-center">
                <CardContent className="p-6">
                  <div className="mb-3 text-5xl">{item.icon}</div>
                  <h4 className="mb-2 font-bold">{item.title}</h4>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
